import { PaymentProofsCard } from "@/components/service-order/PaymentProofsCard";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToneBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PAYMENT_METHODS, SALE_STATUSES, money, labelFrom } from "@/lib/constants";
import { Wallet, Plus, Lock, ShieldAlert, CheckCircle2 } from "lucide-react";

export function FinancePanel({ serviceOrder, onChanged, canAuthorizeCredit }) {
  const [fin, setFin] = useState(null);
  const [sale, setSale] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState({ amount: "", method: "cash", reference: "", notes: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [creditAuth, setCreditAuth] = useState(false);
  const [creditNote, setCreditNote] = useState("");

  async function load() {
    try {
      const [{ data: saleData }, { data: pays }] = await Promise.all([
        api.get(`/service-orders/${serviceOrder.id}/sale`),
        api.get(`/service-orders/${serviceOrder.id}/payments`),
      ]);
      setSale(saleData.sale);
      setFin(saleData.financials);
      setPayments(pays);
    } catch (err) { toast.error(formatApiError(err)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serviceOrder.id]);

  async function addPayment() {
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error("Monto requerido");
    setSaving(true);
    try {
      await api.post(`/service-orders/${serviceOrder.id}/payments`, {
        amount: Number(payForm.amount),
        method: payForm.method,
        reference: payForm.reference || null,
        notes: payForm.notes || null,
      });
      toast.success("Anticipo registrado");
      setPayOpen(false); setPayForm({ amount: "", method: "cash", reference: "", notes: "" });
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  }

  async function closeSale() {
    try {
      await api.post(`/service-orders/${serviceOrder.id}/sale/close`, {
        credit_authorized: creditAuth,
        credit_authorization_notes: creditNote || null,
      });
      toast.success("Venta cerrada");
      setCloseOpen(false); setCreditAuth(false); setCreditNote("");
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  if (!fin) return <div className="text-sm text-muted-foreground">Cargando finanzas…</div>;

  const noQuote = fin.total <= 0;

  return (
    <div className="space-y-4" data-testid="panel-finance">
      <div>
        <h3 className="text-lg tracking-tight">Anticipos y cierre</h3>
        <p className="text-xs text-muted-foreground">
          Registra pagos ligados al folio y cierra la venta cuando el balance esté saldado o el crédito autorizado.
        </p>
      </div>

      <PaymentProofsCard serviceOrder={serviceOrder} />

      {noQuote ? (
        <Card className="border-dashed border-border p-8 text-center bg-white">
          <Wallet className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-muted-foreground">
            Aprueba una cotización primero para habilitar el registro de anticipos.
          </p>
        </Card>
      ) : (
        <>
          {/* Financial summary */}
          <Card className="border-border p-5" data-testid="finance-summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="text-2xl font-semibold mono" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="fin-total">{money(fin.total)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Pagado</div>
                <div className="text-2xl font-semibold mono text-emerald-700" data-testid="fin-paid">{money(fin.paid)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Balance</div>
                <div className={`text-2xl font-semibold mono ${fin.balance > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="fin-balance">
                  {money(fin.balance)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Venta</div>
                <div className="mt-1"><ToneBadge list={SALE_STATUSES} value={fin.sale_status || "open"} /></div>
                {fin.sale_number && <div className="mono text-xs text-muted-foreground mt-1">{fin.sale_number}</div>}
              </div>
            </div>
            {fin.credit_authorized && (
              <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-800">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <b>Crédito autorizado</b> por {sale?.credit_authorized_by_name || "gerente"}.
                  {sale?.credit_authorization_notes && <> {sale.credit_authorization_notes}</>}
                </div>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {fin.sale_status !== "closed" && fin.balance > 0 && (
                <Button className="btn-tenant" size="sm" onClick={() => setPayOpen(true)} data-testid="btn-add-payment">
                  <Plus className="h-4 w-4 mr-2" /> Registrar anticipo
                </Button>
              )}
              {fin.sale_status !== "closed" && (
                <Button variant="outline" size="sm" onClick={() => setCloseOpen(true)} data-testid="btn-close-sale">
                  <Lock className="h-4 w-4 mr-2" /> Cerrar venta
                </Button>
              )}
              {fin.sale_status === "closed" && (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Venta cerrada
                </Badge>
              )}
            </div>
          </Card>

          {/* Payments list */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Historial de pagos</div>
            {payments.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center bg-white">
                Sin pagos registrados.
              </div>
            ) : (
              <Card className="border-border">
                <ul className="divide-y divide-border">
                  {payments.map((p) => (
                    <li key={p.id} className="p-3 flex items-start justify-between" data-testid={`payment-${p.id}`}>
                      <div>
                        <div className="text-sm font-medium mono">{money(p.amount)}</div>
                        <div className="text-xs text-muted-foreground">
                          {labelFrom(PAYMENT_METHODS, p.method)}
                          {p.reference && <> · {p.reference}</>}
                          {" · "}{p.received_at?.slice(0, 16).replace("T", " ")}
                        </div>
                        {p.notes && <div className="text-xs text-slate-700 mt-1">{p.notes}</div>}
                      </div>
                      <Badge variant="secondary">{p.status}</Badge>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Payment dialog */}
      <AlertDialog open={payOpen} onOpenChange={setPayOpen}>
        <AlertDialogContent data-testid="dialog-payment">
          <AlertDialogHeader>
            <AlertDialogTitle>Registrar anticipo</AlertDialogTitle>
            <AlertDialogDescription>
              Balance pendiente: <b>{money(fin.balance)}</b>. El monto no puede excederlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Monto *</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" /></div>
            <div className="space-y-2">
              <Label>Método</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Referencia</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="pay-ref" placeholder="Folio interno, últimos 4 dígitos, etc." /></div>
            <div className="space-y-2"><Label>Notas</Label><Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="pay-notes" /></div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={addPayment} disabled={saving} className="btn-tenant" data-testid="pay-save">
              {saving ? "Guardando…" : "Registrar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close sale dialog */}
      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent data-testid="dialog-close-sale">
          <AlertDialogHeader>
            <AlertDialogTitle>Cerrar venta</AlertDialogTitle>
            <AlertDialogDescription>
              Al cerrar la venta se descuenta el inventario de las refacciones cotizadas y la unidad
              queda lista para entrega. Total <b>{money(fin.total)}</b> · Balance <b>{money(fin.balance)}</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {fin.balance > 0.005 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-3" data-testid="close-credit-block">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Autorizar crédito</Label>
                  <p className="text-xs text-amber-800">Requerido por balance pendiente. Solo dueño/gerente.</p>
                </div>
                <Switch checked={creditAuth} onCheckedChange={setCreditAuth} disabled={!canAuthorizeCredit} data-testid="close-credit-switch" />
              </div>
              {creditAuth && (
                <Textarea rows={2} value={creditNote} onChange={(e) => setCreditNote(e.target.value)} placeholder="Motivo de la autorización (obligatorio para auditoría)" data-testid="close-credit-note" />
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={closeSale} className="btn-tenant" data-testid="close-sale-confirm">Cerrar venta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
